import { CoveringEmail, DeadlineKind, EmailKey, PreviewEmailKey, PreviewRecipient, StepCard, StepOptions, Worklist } from '../../../api/cafAutomation';
import { CatalogueRef, CatalogueReviews } from '../../../api/emailCatalogue';
import { isProviderEmail, sameRef } from '../../email-catalogue/reviewProofs';

/** The workflow window's step cards: rules, words and small helpers (mirrors WF/simple_steps.py). */
export type CardKind = StepCard['kind'];

export const ENDING_KINDS: CardKind[] = ['done', 'archive', 'manual'];
export const MAX_STEPS = 20;

export const STEP_TITLES: Record<CardKind, string> = {
  attach: 'Attach to the claim', note: 'Add a note', email: 'Send an email', catalogue_email: 'Send an email',
  forward: 'Forward received email to client', wait: 'Wait for the client', cancel_claim: 'Cancel the claim',
  set_deadline: 'Set deadline', done: 'Mark done', archive: 'Archive', manual: 'Put on list',
};

/** The Lender task lists in plain words (the server's step options carry the same labels). */
export const WORKLIST_NAMES: Record<Worklist, string> = {
  final_response: 'Final response', provisional: 'Provisional', needs_reply: 'Needs a reply',
  not_sure: 'Not sure', needs_checking: 'Needs checking',
};
export const DEADLINE_NAMES: Record<DeadlineKind, string> = {
  objection: 'Objection deadline (1 month)', fos: 'FOS deadline (6 months)',
};
const DEADLINE_CHIPS: Record<DeadlineKind, string> = { objection: 'Set objection deadline', fos: 'Set FOS deadline' };
const DEADLINE_WORDS: Record<DeadlineKind, string> = { objection: 'objection', fos: 'FOS' };
const listName = (worklist: Worklist | undefined): string => WORKLIST_NAMES[worklist ?? 'not_sure'];

/** Final review C8: an older backend has no lists, so its manual ending is "Mark as manual". */
export const MARK_AS_MANUAL = 'Mark as manual';
/** Whether the server's step options offer lists; undefined until the options are in. */
export const listsOffered = (options: StepOptions | null | undefined): boolean | undefined =>
  (options ? Boolean(options.worklists?.length) : undefined);
/** Without step options to ask, a card from a lists-aware server always carries its list: the
 *  server adds it when it reads a workflow back, and newCard adds it only when lists are offered. */
const onList = (card: Extract<StepCard, { kind: 'manual' }>, lists?: boolean): boolean => lists ?? card.worklist !== undefined;
const endingWords = (lists: boolean) => `Mark done, Archive or ${lists ? STEP_TITLES.manual : MARK_AS_MANUAL}`;

export const EMAIL_NAMES: Record<PreviewEmailKey, string> = {
  loa: 'Letter of authority request', multi_rep: 'Multi-rep notice', cancel_confirmation: 'Reaffirm authority', catalogue_email: 'Catalogue email',
  cancel_notice: 'Cancellation email', forward: "Forward the lender's email to the client",
};

/** The preview's recipient label: the lender's own email is called out; client emails need no label. */
export const recipientLabel = (recipient: PreviewRecipient): string => recipient === 'original_sender' ? 'To the original sender'
  : ['lender', 'approved_lender'].includes(recipient) ? 'To the lender' : 'To the client';

/**
 * Step refusals arrive as 400 with path 'steps[N]'. Returns N so the window can point at the card, or null.
 */
export const stepIndexFromPath = (path: unknown): number | null => {
  const match = typeof path === 'string' ? /^steps\[(\d+)\]/.exec(path) : null;
  return match ? Number(match[1]) : null;
};

/** Send a reminder is not a card in Phase 1. Set deadline is offered only when the server sends deadline_kinds. */
export const MENU_KINDS: CardKind[] = ['attach', 'note', 'email', 'forward', 'wait', 'cancel_claim', 'set_deadline', 'done', 'archive', 'manual'];

export const isConfiguredForward = (card: StepCard): card is Extract<StepCard, { kind: 'forward' }> & { covering_email: CoveringEmail } =>
  card.kind === 'forward' && Object.prototype.hasOwnProperty.call(card, 'covering_email');

export const stepTitle = (card: StepCard, lists?: boolean): string => {
  if (card.kind === 'forward' && !isConfiguredForward(card)) return EMAIL_NAMES.forward;
  if (card.kind === 'manual') return onList(card, lists) ? `Put on list: ${listName(card.worklist)}` : MARK_AS_MANUAL;
  return STEP_TITLES[card.kind];
};

export const forwardReviewKey = (index: number): string => `forward:s${index + 1}`;

const CHIPS: Record<CardKind, string> = {
  attach: 'Attach', note: 'Add a note', email: 'Send an email', catalogue_email: 'Send catalogue email', forward: 'Forward to the client', wait: 'Wait',
  cancel_claim: 'Cancel the claim', set_deadline: 'Set deadline', done: 'Mark done', archive: 'Archive', manual: 'Put on list',
};
const EMAIL_CHIPS: Record<EmailKey, string> = {
  loa: 'Send LOA', multi_rep: 'Send multi-rep email', cancel_confirmation: 'Reaffirm authority',
};

export const isEnding = (card: StepCard): boolean => ENDING_KINDS.indexOf(card.kind) !== -1;
export const emailsClient = (card: StepCard): boolean => card.kind === 'email' || card.kind === 'forward' || card.kind === 'catalogue_email';
/** Sends anything that needs a person to have looked at it: client emails, and a Cancel the claim card's notices. */
export const sendsEmail = (card: StepCard): boolean => emailsClient(card) || card.kind === 'cancel_claim';

export interface StepProblem { index: number | null; message: string }

/** `lists`: whether the server offers lists (listsOffered); it only changes the words. */
export const stepProblems = (cards: StepCard[], lists = true): StepProblem[] => {
  if (!cards.length) return [{ index: null, message: `Add steps, ending with ${endingWords(lists)}.` }];
  const problems: StepProblem[] = [];
  if (cards.length > MAX_STEPS) problems.push({ index: null, message: `A workflow can have at most ${MAX_STEPS} steps.` });
  if (cards[0].kind !== 'attach') {
    problems.push({ index: 0, message: 'Start with Attach to the claim. Every other step acts on the claim it attaches.' });
  }
  let seenEmail = false;
  cards.forEach((card, index) => {
    if (index > 0 && card.kind === 'attach') problems.push({ index, message: 'Attach to the claim can only be the first step.' });
    if (index < cards.length - 1 && isEnding(card)) {
      const title = card.kind === 'manual' && !lists ? MARK_AS_MANUAL : STEP_TITLES[card.kind];
      problems.push({ index, message: `Nothing runs after "${title}". Move it to the end, or remove the steps after it.` });
    }
    if (card.kind === 'note' && !card.text.trim()) problems.push({ index, message: 'Write the note, or remove this step.' });
    if (isConfiguredForward(card) && !isProviderEmail(card.covering_email)) {
      if (!card.covering_email?.template_id.trim()) problems.push({ index, message: 'Choose a covering email for this step.' });
      if (!card.covering_email?.subject.trim()) problems.push({ index, message: 'Write a subject for the covering email.' });
    }
    if (isConfiguredForward(card) && isProviderEmail(card.covering_email) && !card.covering_email.catalogue_ref.option_id) problems.push({ index, message: 'Choose a covering email for this step.' });
    if (card.kind === 'catalogue_email' && !card.catalogue_ref.option_id) problems.push({ index, message: 'Choose an email option.' });
    if (emailsClient(card)) seenEmail = true;
    else if (card.kind === 'wait' && !seenEmail) {
      problems.push({ index, message: 'Wait for the client must come after a step that emails the client.' });
    }
  });
  const deadlines = cards.map((card, index) => (card.kind === 'set_deadline' ? index : -1)).filter((index) => index >= 0);
  if (deadlines.length > 1) problems.push({ index: deadlines[1], message: 'A workflow can set one deadline. Remove the extra Set deadline step.' });
  const last = cards[cards.length - 1];
  const only = deadlines.length === 1 ? cards[deadlines[0]] : null;
  if (only && only.kind === 'set_deadline' && only.deadline !== 'fos' && last.kind === 'manual' && last.worklist === 'final_response') {
    problems.push({ index: deadlines[0], message: 'The Final response list only takes the FOS deadline. Choose FOS deadline (6 months), or put the email on another list.' });
  }
  if (!isEnding(cards[cards.length - 1])) {
    problems.push({ index: cards.length - 1, message: `The last step must be ${endingWords(lists)}.` });
  }
  return problems;
};

export const templatesOf = (card: StepCard, options: StepOptions | null): string[] => {
  if (isConfiguredForward(card)) return !isProviderEmail(card.covering_email) && card.covering_email?.template_id ? [card.covering_email.template_id] : [];
  if (!options) return [];
  if (card.kind === 'forward') return options.forward_template_id ? [options.forward_template_id] : [];
  if (card.kind === 'cancel_claim') return card.catalogue_emails ? [] : (options.cancel_templates?.[card.reason] ?? []).filter(Boolean);
  if (card.kind !== 'email') return [];
  const option = options.emails.find((item) => item.key === card.email);
  if (!option) return [];
  return [!card.catalogue_ref && option.template_id, !card.reminder_catalogue_ref && option.reminder && option.reminder_template_id].filter(Boolean) as string[];
};

export const catalogueRefs = (card: StepCard, index: number): Record<string, CatalogueRef> => {
  const node = `s${index + 1}`;
  if (card.kind === 'catalogue_email') return { [`${node}/email`]: card.catalogue_ref };
  if (card.kind === 'forward' && isProviderEmail(card.covering_email)) return { [`${node}/email`]: card.covering_email.catalogue_ref };
  if (card.kind === 'email') return { ...(card.catalogue_ref ? { [`${node}/email`]: card.catalogue_ref } : {}),
    ...(card.reminder_catalogue_ref ? { [`${node}/reminder`]: card.reminder_catalogue_ref } : {}) };
  if (card.kind === 'cancel_claim') return Object.fromEntries(Object.entries(card.catalogue_emails || {}).map(([role, ref]) => [`${node}/${role}`, ref]));
  return {};
};
export const catalogueReviewedFor = (cards: StepCard[], reviewed: CatalogueReviews): CatalogueReviews => Object.fromEntries(
  cards.flatMap((card, index) => Object.entries(catalogueRefs(card, index))).filter(([key, ref]) => sameRef(ref, reviewed[key]?.catalogue_ref))
    .map(([key]) => [key, reviewed[key]]));

export const isChecked = (card: StepCard, options: StepOptions | null, reviewed: Record<string, string>, index?: number, catalogue: CatalogueReviews = {}): boolean => {
  const templates = templatesOf(card, options);
  const refs = index === undefined ? [] : Object.entries(catalogueRefs(card, index));
  if (isConfiguredForward(card) && !isProviderEmail(card.covering_email) && (index === undefined || !reviewed[forwardReviewKey(index)])) return false;
  return templates.length + refs.length > 0 && templates.every((id) => Boolean(reviewed[id]))
    && refs.every(([key, ref]) => {
      const current = options?.catalogue_options?.find(item => item.ref.option_id === ref.option_id);
      return sameRef(ref, catalogue[key]?.catalogue_ref) && (!current || current.status === 'ready' && sameRef(current.ref, ref)
        && current.provider?.evidence.fingerprint === catalogue[key]?.provider_fingerprint);
    });
};

export const clientEmailCount = (cards: StepCard[]): number => cards.filter(sendsEmail).length;

export const checkedCount = (cards: StepCard[], options: StepOptions | null, reviewed: Record<string, string>, catalogue: CatalogueReviews = {}): number =>
  cards.filter((card, index) => sendsEmail(card) && isChecked(card, options, reviewed, index, catalogue)).length;

/** Only the ticks for templates the list still uses. */
export const reviewedFor = (
  cards: StepCard[], options: StepOptions | null, reviewed: Record<string, string>,
): Record<string, string> => {
  const used: Record<string, string> = {};
  cards.forEach((card, index) => {
    templatesOf(card, options).forEach((id) => { if (reviewed[id]) used[id] = reviewed[id]; });
    const key = forwardReviewKey(index);
    if (isConfiguredForward(card) && !isProviderEmail(card.covering_email) && reviewed[key]) used[key] = reviewed[key];
  });
  return used;
};

export const chipLabel = (card: StepCard, lists?: boolean): string => {
  if (card.kind === 'email') return EMAIL_CHIPS[card.email];
  if (card.kind === 'wait') return `Wait ${card.days} days`;
  if (card.kind === 'set_deadline') return DEADLINE_CHIPS[card.deadline];
  if (card.kind === 'manual') return onList(card, lists) ? `Put on list: ${listName(card.worklist)}` : MARK_AS_MANUAL;
  return CHIPS[card.kind];
};

const phrase = (card: StepCard, options: StepOptions | null): string => {
  if (card.kind === 'attach') return 'attach it to the claim';
  if (card.kind === 'note') return 'add a note to the claim';
  if (card.kind === 'catalogue_email') return `send the selected email "${options?.catalogue_options?.find(item => sameRef(item.ref, card.catalogue_ref))?.config.name || card.catalogue_ref.option_id}"`;
  if (card.kind === 'email') {
    const option = options?.emails.find((item) => item.key === card.email);
    return `email the client "${EMAIL_NAMES[card.email]}"${option?.reminder ? ' (it sends its own reminder)' : ''}`;
  }
  if (card.kind === 'forward') return (card.forwarding_scope === 'closed_claim_default' ? 'for cancelled claims only, ' : '') + "send the client a covering email with the lender's email as a PDF and all its original attachments";
  if (card.kind === 'wait') return `wait ${card.days} days for the client`;
  if (card.kind === 'cancel_claim') return `cancel the claim ("${card.reason}"${card.fee === 'none' ? ', no fee' : ''})`;
  if (card.kind === 'set_deadline') return `record the ${DEADLINE_WORDS[card.deadline]} deadline on the claim and remind staff 7, 3 and 1 days before`;
  if (card.kind === 'done') return 'mark the task done';
  if (card.kind === 'archive') return 'archive the email';
  if (card.kind === 'manual' && onList(card, listsOffered(options))) return `put it on the "${listName(card.worklist)}" list`;
  return 'mark it as manual';
};

export const stepSentence = (cards: StepCard[], options: StepOptions | null): string => {
  if (!cards.length) return '';
  const parts = cards.map((card) => phrase(card, options));
  const body = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')}, then ${parts[parts.length - 1]}`;
  return `When the AI follows an example that uses this workflow, ${body}.`;
};

const lower = (text: string) => text.charAt(0).toLowerCase() + text.slice(1);

export const suggestName = (cards: StepCard[], lists?: boolean): string => {
  const parts = cards.map((card, index) => (index === 0 ? chipLabel(card, lists) : lower(chipLabel(card, lists))));
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
};

export const newCard = (kind: CardKind, options: StepOptions | null): StepCard => {
  if (kind === 'catalogue_email') return { kind, catalogue_ref: { option_id: '', revision: 0, config_hash: '' } };
  if (kind === 'forward') return options?.catalogue_options ? { kind: 'forward', covering_email: { delivery_mode: 'provider_template_v1', catalogue_ref: { option_id: '', revision: 0, config_hash: '' } } }
    : { kind: 'forward', covering_email: { template_id: '', subject: '', tokens: {} } };
  if (kind === 'note') return { kind: 'note', text: '' };
  if (kind === 'email') {
    const first = options?.emails.find((item) => item.available);
    return { kind: 'email', email: first ? first.key : 'loa' };
  }
  if (kind === 'wait') return { kind: 'wait', days: 7 };
  if (kind === 'cancel_claim') return { kind: 'cancel_claim', reason: options?.cancel_reasons[0] ?? 'Duplicate claim', fee: 'usual' };
  if (kind === 'manual') return options?.worklists?.length ? { kind: 'manual', note: '', worklist: 'not_sure' } : { kind: 'manual', note: '' };
  if (kind === 'set_deadline') return { kind: 'set_deadline', deadline: 'objection' };
  return { kind } as StepCard;
};

export const moveCard = (cards: StepCard[], index: number, delta: -1 | 1): StepCard[] => {
  const target = index + delta;
  if (target < 0 || target >= cards.length) return cards;
  const next = cards.slice();
  const moved = next[index];
  next[index] = next[target];
  next[target] = moved;
  return next;
};
